import { api } from './client';

import type { AssessmentSituation, CampaignV4, TrackingV4 } from '@/types/campaign';
import type { ParticipantV4 } from '@/types/participant';

// TODO: remove fake data once the v4 backend is available
const USE_FAKE_DATA = false;

const fakeContext = `Vous travaillez pour XTP. À l'origine une petite entreprise familiale, l'excellence de son savoir-faire et la qualité de ses constructions lui ont permis de devenir un leader reconnu dans la région. L'entreprise vient de décrocher le « Projet Horizon », un nouveau quartier urbain ambitieux en centre-ville qui allie patrimoine historique et innovation moderne. L'avenir de toute l'entreprise dépend de la réussite de ce projet.

Les personnes suivantes sont étroitement associées au projet :
• Camille est la conductrice de travaux affectée au projet
• Gérard est l'architecte de la société
• Vincent est l'ingénieur structure
• Jade est la responsable logistique
• Kevin est le nouveau DG de XTP

Vous occupez le poste de directeur du projet Horizon, tout le monde sur le projet vous rend compte, et vous rendez compte à Kevin.`;

function fakeSituation(metaHuman: string): AssessmentSituation {
  return {
    id: `situation_${metaHuman}`,
    sidecar: { schema_version: "1", provider: "eleven_labs", agent_id: `agent_${metaHuman}` },
    app: { schema_version: "1", meta_human: metaHuman, environment: "studio" },
  };
}

const fakeSituations: AssessmentSituation[] = ["camille", "gerard", "vincent", "jade", "kevin"].map(fakeSituation);

function fakeCampaign(id: string, headline: string, jobTitle: string, situationCount: number): CampaignV4 {
  return {
    schema_version: "1.0.0",
    campaign_version: "4.0.0",
    created_at: "2026-09-01T00:00:00+00:00",
    updated_at: "2026-09-01T00:00:00+00:00",
    organization_id: "018ed32a-cb88-7483-bd6a-08c5a7708ddf",
    id,
    public_invite_id: "",
    archived: false,
    auto_activate: true,
    campaign: {
      parent_workspace_id: "a6c6df1e-6d44-47e9-9904-741254eb4ea0",
      internal_name: headline,
      headline,
      tagline: "",
      job_title: jobTitle,
      locale: "fr-FR",
    },
    branding: {
      banner_image_id: "",
      background_image_id: "",
      text_color: "#000000",
      highlight_color: "#F2CB36",
    },
    assessment: {
      description: fakeContext,
      situations: fakeSituations.slice(0, situationCount),
      skills: [],
    },
  };
}

function fakeParticipant(campaign: CampaignV4, state: number, creationTime: string): ParticipantV4 {
  return {
    participant_id: `participant-${campaign.id}`,
    campaign_id: campaign.id,
    candidate_id: "fake-candidate",
    inviter_id: "fake-inviter",
    activated: true,
    pending_email: "",
    pending_first_name: "",
    pending_last_name: "",
    state,
    user_comment: "",
    creation_time: creationTime,
    last_reminder_time: creationTime,
    results: null,
    progress: 0,
    instance_reservation: null,
    campaign,
  };
}

const fakeAssessments: ParticipantV4[] = [
  fakeParticipant(fakeCampaign("fake-v4-1", "Évaluation du Potentiel Managérial", "Directeur de projet", 5), 0, "2026-09-28T09:00:00+02:00"),
  fakeParticipant(fakeCampaign("fake-v4-2", "Leadership & Communication", "Chef d'équipe travaux", 3), 0, "2026-09-25T09:00:00+02:00"),
  fakeParticipant(fakeCampaign("fake-v4-3", "Gestion de situations de crise", "Responsable logistique", 4), 0, "2026-09-30T09:00:00+02:00"),
  fakeParticipant(fakeCampaign("fake-v4-4", "Négociation & Relation client", "Chargé d'affaires", 4), 1, "2026-09-15T09:00:00+02:00"),
  fakeParticipant(fakeCampaign("fake-v4-5", "Onboarding Managers 2026", "Manager de proximité", 2), 2, "2026-09-01T09:00:00+02:00"),
  fakeParticipant(fakeCampaign("fake-v4-6", "Prise de poste – Conduite de projet", "Conducteur de travaux", 5), 2, "2026-09-08T09:00:00+02:00"),
];

// Indexes of the completed situations for each fake campaign
const fakeCompleted: Record<string, number[]> = {
  "fake-v4-4": [0, 1],
  "fake-v4-5": [0, 1],
  "fake-v4-6": [0, 1, 2, 4],
};

function isCampaignV4OrMore(campaign: { campaign_version?: string }): boolean {
  if (!campaign?.campaign_version) return false;
  return parseInt(campaign.campaign_version.split('.')[0]) >= 4;
}

export const assessmentApi = {
  // Candidate's assessments, restricted to v4+ campaigns
  getCandidateAssessmentsV4: async (): Promise<ParticipantV4[]> => {
    if (USE_FAKE_DATA) return fakeAssessments;

    const { data } = await api.get('/candidates/me/assessments');
    return (data.assessments || []).filter((a: ParticipantV4) => isCampaignV4OrMore(a.campaign));
  },

  getTracking: async (participationId: string): Promise<TrackingV4> => {
    if (USE_FAKE_DATA) {
      const campaign = fakeAssessments.find((a) => a.participant_id === participationId)?.campaign;
      const situations = campaign?.assessment.situations || [];
      return {
        schema_version: "1.0.0",
        campaign_version: "4.0.0",
        total_situations: situations.map((s) => s.id),
        completed_situations: (fakeCompleted[participationId] || []).map((i) => ({
          situation: situations[i].id,
          object: `2026-09-22_${situations[i].id}.json`,
        })),
      };
    }

    const { data } = await api.get(`/api/v1/participations/${participationId}/state`);
    return data;
  },
};
